import { z } from 'zod';
import { isoDateStringSchema, objectIdSchema } from './common.js';
import { SKETCH_SCENE_MAX_BYTES, sketchSceneSchema } from './sketch-scene.js';

/**
 * The Design Board — what the shop and the customer agreed the garment should look like.
 *
 * A booking is settled in pictures long before it is settled in words: a screenshot the customer
 * sent, two neck designs the shop offered, the one she picked. Today that conversation lives in
 * WhatsApp, which means it is lost the moment somebody clears a chat, and the tailor cutting the
 * blouse is working from a description of a picture rather than the picture.
 *
 * A card is one design idea. It belongs to ONE PERSON OR ONE ORDER — see `DesignBoardOwner` — and,
 * on an order, optionally to one ITEM within it: a card about the blouse, not about the whole
 * booking, because a wedding order is four garments and a board that cannot say which one it is
 * about is a board nobody trusts.
 *
 * The card is the unit that moves, not the image: a card is shown to the customer, and she
 * approves or rejects THAT — the title, the notes and the pictures together. Hence the status
 * lives on the card and the images are just its ordered contents.
 *
 * Contract only. The models, the endpoints and the tenant checks are task 16; the board itself
 * is task 17.
 */

/**
 * Where a card has got to.
 *
 * `idea` is the shop thinking out loud, and is what a card is created at. `shared` means it has
 * been put in front of the customer — the moment worth recording, because it is the difference
 * between "we never showed her" and "she has seen it and not replied". `approved` / `rejected`
 * are her answer.
 */
export const DESIGN_CARD_STATUSES = ['idea', 'shared', 'approved', 'rejected'] as const;
export type DesignCardStatus = (typeof DESIGN_CARD_STATUSES)[number];
export const designCardStatusSchema = z.enum(DESIGN_CARD_STATUSES);

export const DESIGN_CARD_TITLE_MAX_LENGTH = 120;
export const DESIGN_CARD_NOTES_MAX_LENGTH = 2000;

/** Most images one card may hold. Past this it is not a design idea, it is an album. */
export const DESIGN_CARD_MAX_MEDIA = 20;

/**
 * `entityType` for an image uploaded from the board itself; `entityId` is the card id.
 *
 * A card's `mediaIds` may also point at images picked out of the shop gallery, which carry
 * `GALLERY_MEDIA_ENTITY_TYPE` instead — the card does not care where a picture came from, only
 * that it resolves inside the same shop. Matches the media module's `ENTITY_TYPE_PATTERN`.
 */
export const DESIGN_CARD_MEDIA_ENTITY_TYPE = 'design_card';

export const designCardTitleSchema = z
  .string()
  .trim()
  .min(1, 'Give the design a name')
  .max(DESIGN_CARD_TITLE_MAX_LENGTH);

/**
 * The pictures on the card, in the order they are shown — the ARRAY ORDER IS THE DISPLAY ORDER,
 * so there is no per-image sort field to keep in step with it.
 *
 * Ids may be images uploaded from the board (`design_card`) or picked out of the shop gallery
 * (`gallery`). Task 16 checks that every id resolves inside the caller's tenant; the schema can
 * only say they are well-formed ids and that there are not too many.
 */
export const designCardMediaIdsSchema = z.array(objectIdSchema).max(DESIGN_CARD_MAX_MEDIA);

const designCardFieldsSchema = z.object({
  title: designCardTitleSchema,
  /**
   * Which garment this is about, or null for the order as a whole. The service verifies the item
   * actually belongs to that order — the schema cannot, since it never sees the order.
   *
   * Only an ORDER has items, so this is refused on an enquiry's or a customer's board: before a
   * booking exists there is no garment row to point at, and a card that named one would be
   * pointing at somebody else's order.
   */
  orderItemId: objectIdSchema.nullish(),
  mediaIds: designCardMediaIdsSchema,
  /** Free text: "keep the sleeve length from the last one, but this neck." */
  notes: z.string().trim().max(DESIGN_CARD_NOTES_MAX_LENGTH).nullish(),
  status: designCardStatusSchema,
});

/**
 * Creating a card.
 *
 * The OWNER is deliberately absent: it comes from the route path — `POST /orders/:orderId` or
 * `/customers/:customerId` + `/design-cards`. A body that could name a different owner than the
 * URL is a body that can be pointed at somebody else's board.
 *
 * A title alone is enough — that is what "add a card, photos to follow" looks like at the
 * counter.
 */
export const createDesignCardSchema = designCardFieldsSchema.extend({
  mediaIds: designCardMediaIdsSchema.default([]),
  status: designCardStatusSchema.default('idea'),
});
export type CreateDesignCardInput = z.input<typeof createDesignCardSchema>;

/** Every field optional, but `{}` is a mistake worth naming rather than a silent 200. */
export const updateDesignCardSchema = designCardFieldsSchema
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateDesignCardInput = z.input<typeof updateDesignCardSchema>;

/** Dragging cards around the board. Same shape as the media module's `reorderMediaSchema`. */
export const reorderDesignCardsSchema = z.object({
  /** Card ids in their new order. Anything omitted keeps its relative position after these. */
  order: z.array(objectIdSchema).min(1).max(200),
});
export type ReorderDesignCardsInput = z.input<typeof reorderDesignCardsSchema>;

/**
 * Filtering the board. Unpaginated, like the gallery's folders: an order has a handful of cards,
 * and a board assembled from pages is not a board.
 */
export const listDesignCardsQuerySchema = z.object({
  orderItemId: objectIdSchema.optional(),
  status: designCardStatusSchema.optional(),
});
export type ListDesignCardsQuery = z.infer<typeof listDesignCardsQuerySchema>;

/**
 * WHOSE BOARD A CARD IS ON — an order or a customer, and exactly one of the two.
 *
 * The design is argued out BEFORE the booking exists, over WhatsApp, while she is still deciding —
 * so a customer has a board of her own as well as each order having one. (Cards once agreed on an
 * enquiry were moved onto its customer by the lead → customer data migration; the table's
 * `lead_id` / `adopted_from_lead_id` columns are legacy and no API reads or writes them.)
 *
 * One hook takes it, one panel renders it, and the two screens differ in what they pass rather
 * than in what they draw.
 */
export type DesignBoardOwner =
  { kind: 'order'; orderId: string } | { kind: 'customer'; customerId: string };

/** The owner's own id, for query keys and URLs — the one field that is never null. */
export function designBoardOwnerId(owner: DesignBoardOwner): string {
  switch (owner.kind) {
    case 'order':
      return owner.orderId;
    default:
      return owner.customerId;
  }
}

export const designCardSchema = z.object({
  id: objectIdSchema,
  /**
   * Exactly one of these two is set; the other is null. The server holds that invariant (see
   * `design-card.model.ts`) — a schema cannot say "one of these" without turning every reader
   * into a discriminated-union match for no gain.
   */
  orderId: objectIdSchema.nullable(),
  customerId: objectIdSchema.nullable(),
  orderItemId: objectIdSchema.nullable(),
  title: z.string(),
  mediaIds: z.array(objectIdSchema),
  notes: z.string().nullable(),
  status: designCardStatusSchema,
  /**
   * Whether a DRAWING has been saved for this card — see the sketch section below.
   *
   * Denormalised onto the card rather than answered by a lookup, because a board read is
   * unpaginated and this is the only thing it needs to know about a scene that may be a megabyte
   * of stroke data. The sketch write maintains it in the same transaction, so the flag and the
   * scene cannot disagree.
   */
  hasSketch: z.boolean(),
  /**
   * The flattened PNG of that drawing, if the export reached storage. This is one of the ids in
   * `mediaIds` — it is named separately so that re-saving a sketch REPLACES its rendering rather
   * than appending a second copy of the same picture to the card every time.
   *
   * Null while a card has a scene but no rendering: the drawing saves first and the export is
   * allowed to fail after it, because losing a picture that can be regenerated is a nuisance and
   * losing the strokes is not.
   */
  sketchMediaId: objectIdSchema.nullable(),
  sortOrder: z.number().int(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type DesignCard = z.infer<typeof designCardSchema>;

export const designCardListSchema = z.object({ items: z.array(designCardSchema) });
export type DesignCardList = z.infer<typeof designCardListSchema>;

// --- The sketch: a card's freehand drawing ----------------------------------------------------

/**
 * THE DRAWING ON A CARD — the neckline argued out with a pen rather than found in a photograph.
 *
 * The card board answers "what did she agree to". It cannot answer "what if the neck were
 * squarer", because there is no photograph of a garment nobody has made yet. That is what a
 * sketch is for, and it is deliberately not a second kind of card: a sketch BELONGS TO one card,
 * carries that card's title, status and notes, and travels with it when an enquiry converts. One
 * idea, one card, whether it arrived as a screenshot or as a drawing.
 *
 * A saved sketch produces two things, and they are not interchangeable:
 *
 *  - the SCENE, the editable drawing itself, stored here as an opaque JSON string and read only
 *    when somebody opens the canvas;
 *  - a flattened PNG, pushed through the ordinary media pipeline onto the card's `mediaIds`, so
 *    the board tile, the lightbox and anything else that shows a card's pictures work on a
 *    drawing without knowing what a scene is.
 *
 * The scene is a STRING and not an object on purpose. The server never reads inside it — it is
 * the drawing tool's format, versioned by the drawing tool — and a schema that claimed to know
 * its shape would have to be revised every time that tool adds an element type. What the server
 * does enforce is a size ceiling and that it parses as JSON, which is the whole of what it can
 * honestly check.
 */

/** Biggest scene we will store. One ceiling for every drawing — see `sketch-scene.ts`. */
export const DESIGN_SKETCH_MAX_BYTES = SKETCH_SCENE_MAX_BYTES;

/** The scene as it goes over the wire: the shared rule in `sketch-scene.ts`. */
export const designSketchSceneSchema = sketchSceneSchema;

/**
 * Saving a drawing. An upsert: the first save creates the scene, every later one replaces it.
 *
 * `previewMediaId` is the PNG the client has already uploaded and attached to the card, handed
 * over so the server can retire the PREVIOUS rendering in the same write. Omitting it leaves
 * whatever rendering the card already had, which is what a save that could not export looks like.
 */
export const saveDesignSketchSchema = z.object({
  scene: designSketchSceneSchema,
  previewMediaId: objectIdSchema.nullish(),
});
export type SaveDesignSketchInput = z.input<typeof saveDesignSketchSchema>;

export const designSketchSchema = z.object({
  cardId: objectIdSchema,
  scene: z.string(),
  updatedAt: isoDateStringSchema,
});
export type DesignSketch = z.infer<typeof designSketchSchema>;
